/**
 * Relationship Sync Utilities
 * Handles bidirectional sync of relationships between characters
 */

import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore'
import { db } from './firebase'

/**
 * Get the reciprocal direction for a relationship
 * @param {string} direction - 'to', 'from', 'mutual', or 'self'
 * @returns {string|null} - The reciprocal direction, or null if unknown
 */
export function getReciprocalDirection(direction) {
  if (direction === 'to') return 'from'
  if (direction === 'from') return 'to'
  if (direction === 'mutual') return 'mutual'
  if (direction === 'self') return 'self' // Alter-egos are bidirectional
  return null
}

/**
 * Create a reciprocal relationship object
 * @param {Object} relationship - The source relationship
 * @param {string} sourceId - The source character's ID
 * @returns {Object|null} - The reciprocal relationship, or null if not applicable
 */
function createReciprocal(relationship, sourceId) {
  const reciprocalDir = getReciprocalDirection(relationship.direction)
  if (!reciprocalDir) return null

  return {
    targetId: sourceId,
    type: relationship.type,
    direction: reciprocalDir,
    notes: '', // Each side keeps independent notes
    hidden: relationship.hidden || false
  }
}

/**
 * Find a matching relationship in an array
 * @param {Array} relationships - Array of relationships
 * @param {string} targetId - Target character ID to match
 * @param {string} type - Relationship type to match
 * @returns {Object|undefined} - The matching relationship or undefined
 */
function findMatchingRelationship(relationships, targetId, type) {
  return relationships.find(r => r.targetId === targetId && r.type === type)
}

/**
 * Sync reciprocal relationships after a character's relationships are updated
 * @param {string} sourceId - The character ID whose relationships were edited
 * @param {Array} originalRels - Original relationships before editing
 * @param {Array} newRels - New relationships after editing
 */
export async function syncReciprocals(sourceId, originalRels, newRels) {
  // Normalize arrays
  const originals = originalRels || []
  const updated = newRels || []

  // Find added relationships (in new but not in original, by targetId + type)
  const added = updated.filter(newRel =>
    !originals.some(orig =>
      orig.targetId === newRel.targetId && orig.type === newRel.type
    )
  )

  // Find removed relationships (in original but not in new)
  const removed = originals.filter(orig =>
    !updated.some(newRel =>
      newRel.targetId === orig.targetId && newRel.type === orig.type
    )
  )

  // Find modified relationships (same targetId + type, different direction or hidden)
  const modified = updated.filter(newRel => {
    const orig = originals.find(o =>
      o.targetId === newRel.targetId && o.type === newRel.type
    )
    return orig && (
      orig.direction !== newRel.direction ||
      orig.hidden !== newRel.hidden
    )
  })

  // Process additions
  for (const rel of added) {
    await addReciprocalToTarget(rel.targetId, createReciprocal(rel, sourceId))
  }

  // Process removals
  for (const rel of removed) {
    await removeReciprocalFromTarget(rel.targetId, sourceId, rel.type)
  }

  // Process modifications
  for (const rel of modified) {
    await updateReciprocalOnTarget(rel.targetId, sourceId, rel.type, createReciprocal(rel, sourceId))
  }
}

/**
 * Add a reciprocal relationship to a target character
 */
async function addReciprocalToTarget(targetId, reciprocal) {
  if (!reciprocal) return

  try {
    const docRef = doc(db, 'characters', targetId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      console.warn(`Target character ${targetId} not found, skipping reciprocal`)
      return
    }

    const targetData = docSnap.data()
    const relationships = targetData.relationships || []

    // Check if reciprocal already exists
    const existingIndex = relationships.findIndex(
      r => r.targetId === reciprocal.targetId && r.type === reciprocal.type
    )

    if (existingIndex >= 0) {
      // Update existing reciprocal (overwrite)
      relationships[existingIndex] = {
        ...relationships[existingIndex],
        direction: reciprocal.direction,
        hidden: reciprocal.hidden
      }
    } else {
      // Add new reciprocal
      relationships.push(reciprocal)
    }

    await updateDoc(docRef, { relationships })
  } catch (error) {
    console.error(`Failed to add reciprocal to ${targetId}:`, error)
  }
}

/**
 * Remove a reciprocal relationship from a target character
 */
async function removeReciprocalFromTarget(targetId, sourceId, type) {
  try {
    const docRef = doc(db, 'characters', targetId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return // Target doesn't exist, nothing to remove
    }

    const targetData = docSnap.data()
    const relationships = targetData.relationships || []

    // Filter out the reciprocal relationship
    const filtered = relationships.filter(
      r => !(r.targetId === sourceId && r.type === type)
    )

    // Only update if something was actually removed
    if (filtered.length !== relationships.length) {
      await updateDoc(docRef, { relationships: filtered })
    }
  } catch (error) {
    console.error(`Failed to remove reciprocal from ${targetId}:`, error)
  }
}

/**
 * Update a reciprocal relationship on a target character
 */
async function updateReciprocalOnTarget(targetId, sourceId, type, reciprocal) {
  if (!reciprocal) return

  try {
    const docRef = doc(db, 'characters', targetId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return
    }

    const targetData = docSnap.data()
    const relationships = targetData.relationships || []

    // Find and update the reciprocal
    const index = relationships.findIndex(
      r => r.targetId === sourceId && r.type === type
    )

    if (index >= 0) {
      relationships[index] = {
        ...relationships[index],
        direction: reciprocal.direction,
        hidden: reciprocal.hidden
      }
      await updateDoc(docRef, { relationships })
    } else {
      // Reciprocal doesn't exist, create it
      await addReciprocalToTarget(targetId, reciprocal)
    }
  } catch (error) {
    console.error(`Failed to update reciprocal on ${targetId}:`, error)
  }
}

/**
 * Backfill all missing reciprocal relationships across all characters
 * This is a one-time operation to sync existing data
 * @returns {Object} Stats about what was synced
 */
export async function backfillAllReciprocals() {
  const stats = {
    charactersProcessed: 0,
    reciprocalsCreated: 0,
    reciprocalsUpdated: 0,
    errors: []
  }

  try {
    // Fetch all characters
    const charactersRef = collection(db, 'characters')
    const snapshot = await getDocs(charactersRef)

    // Build a map of all characters for quick lookup
    const charactersById = {}
    snapshot.forEach(docSnap => {
      charactersById[docSnap.id] = {
        id: docSnap.id,
        ...docSnap.data()
      }
    })

    // Process each character's relationships
    for (const sourceId of Object.keys(charactersById)) {
      const sourceChar = charactersById[sourceId]
      const relationships = sourceChar.relationships || []

      stats.charactersProcessed++

      for (const rel of relationships) {
        const targetId = rel.targetId
        const targetChar = charactersById[targetId]

        if (!targetChar) {
          // Target doesn't exist, skip
          continue
        }

        // Check if reciprocal exists on target
        const targetRels = targetChar.relationships || []
        const existingReciprocal = targetRels.find(
          r => r.targetId === sourceId && r.type === rel.type
        )

        const expectedReciprocal = createReciprocal(rel, sourceId)
        if (!expectedReciprocal) continue

        if (!existingReciprocal) {
          // Reciprocal missing, create it
          targetRels.push(expectedReciprocal)
          charactersById[targetId].relationships = targetRels
          stats.reciprocalsCreated++
        } else if (
          existingReciprocal.direction !== expectedReciprocal.direction ||
          existingReciprocal.hidden !== expectedReciprocal.hidden
        ) {
          // Reciprocal exists but needs updating
          const idx = targetRels.findIndex(
            r => r.targetId === sourceId && r.type === rel.type
          )
          targetRels[idx] = {
            ...targetRels[idx],
            direction: expectedReciprocal.direction,
            hidden: expectedReciprocal.hidden
          }
          charactersById[targetId].relationships = targetRels
          stats.reciprocalsUpdated++
        }
      }
    }

    // Save all modified characters
    const modifiedIds = new Set()

    // Track which characters were modified by checking reciprocal counts
    for (const sourceId of Object.keys(charactersById)) {
      const sourceChar = charactersById[sourceId]
      const relationships = sourceChar.relationships || []

      for (const rel of relationships) {
        const targetId = rel.targetId
        if (charactersById[targetId]) {
          modifiedIds.add(targetId)
        }
      }
    }

    // Actually save to Firestore - only characters that have relationships pointing to them
    for (const charId of Object.keys(charactersById)) {
      const char = charactersById[charId]
      if (char.relationships && char.relationships.length > 0) {
        try {
          const docRef = doc(db, 'characters', charId)
          await updateDoc(docRef, { relationships: char.relationships })
        } catch (err) {
          stats.errors.push(`Failed to update ${char.name || charId}: ${err.message}`)
        }
      }
    }

    return stats
  } catch (error) {
    stats.errors.push(`Backfill failed: ${error.message}`)
    return stats
  }
}
